/**
 * EF-SETTINGS-CONSOLIDATION-V1 (PHASE 1 + PHASE 3): Settings Hub
 * 
 * Central landing page for all settings.
 * Displays 4 categories:
 * - Platform Settings (superadmin only)
 * - Company Settings (tenant admin)
 * - My Preferences (all users)
 * - System (debug tools, system info)
 * 
 * PHASE 3: Added System Version card with platform/PWA version
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { BUILD_VERSION, BUILD_INFO, APP_VERSION } from "../lib/version";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Building2,
  Settings,
  User,
  Wrench,
  ChevronRight,
  Mail,
  Palette,
  CreditCard,
  Plug,
  Shield,
  Server,
  Bug,
  FileText,
  Globe2,
  Info,
  Sparkles,
  Scale,
  Languages,
} from "lucide-react";

// Settings categories with their sub-items
// EVENTSFLO-SETTINGS-HUB-ORGANISATION-AND-MISSING-LINKS-V1
// Reorganised into 5 task-driven groups so every admin page is reachable
// without "+N more" truncation. Cards show ALL items.
const settingsCategories = [
  {
    id: "platform",
    title: "Platform Settings",
    description: "Platform identity, global defaults, and system integrations",
    icon: Building2,
    path: "/settings/platform",
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
    superadminOnly: true,
    items: [
      { label: "Platform Branding", path: "/settings/platform" },
      { label: "Global Defaults", path: "/settings/platform" },
      { label: "Google Maps API", path: "/settings/platform" },
    ],
  },
  // A. Company & Account
  {
    id: "company",
    title: "Company & Account",
    description: "Tenant profile, branding, financial settings, and personal preferences",
    icon: Settings,
    path: "/settings/company/profile",
    color: "text-emerald-500",
    bgColor: "bg-emerald-500/10",
    items: [
      { label: "Company Profile", path: "/settings/company", icon: Building2 },
      { label: "Branding", path: "/settings/company", icon: Palette },
      { label: "Financial Settings", path: "/settings/company", icon: CreditCard },
      // EVENTSFLO-COMPANY-DOCUMENTS-CLIENT-PORTAL-DOWNLOADS-V1
      { label: "Company Documents", path: "/settings/company-documents", icon: FileText },
      { label: "My Preferences", path: "/settings/user", icon: User },
      { label: "System Contacts", path: "/admin/system-contacts", icon: User },
    ],
  },
  // B. Rules, Tasks & Pricing
  // EVENTSFLO-SETTINGS-RULES-LABEL-ACTIVE-VS-AI-REVIEW-ONLY-V1
  {
    id: "rules-tasks",
    title: "Rules, Tasks & Pricing",
    description:
      "Live rule thresholds (Crew Chief / Meal Allowance / Seats per Car), Task Definitions and supplements, bilingual renames, and parser aliases.",
    icon: Scale,
    path: "/rules",
    color: "text-amber-500",
    bgColor: "bg-amber-500/10",
    statusBadge: {
      label: "ACTIVE — affects saved shifts",
      variant: "active",
    },
    items: [
      { label: "Live Rule Settings", path: "/rules", icon: Scale,
        description: "Crew Chief threshold, Meal Allowance hours, Seats per Car. Affects saved shifts." },
      { label: "Task Definitions / Supplements", path: "/task-definitions", icon: Wrench,
        description: "Labour roles, supplements, OOT travel, multipliers, rates." },
      // EVENTSFLO-TASK-DEFINITIONS-PHASE-A-RENAME-CONFIRMATION-V1
      { label: "Task Definition Bilingual Renames", path: "/settings/task-definitions/bilingual-renames", icon: Languages,
        description: "Review safe bilingual task-name updates such as Meal Allowance / Panier repas." },
      // EVENTSFLO-TASK-DEFINITIONS-EXPORT-DEPENDENCY-REPORT-V1
      // EVENTSFLO-TASK-DEFINITIONS-IMPORT-APPLY-SAFE-CHANGES-V1
      { label: "Task Definitions Export / Import", path: "/settings/task-definitions/dependency-report", icon: FileText,
        description: "Download the Task Definitions catalogue, edit in Excel, upload for dry-run validation, then apply selected safe changes. (Search: task, supplements, export, import, dry-run, Excel, XLSX)" },
      { label: "Parser Aliases", path: "/settings/parser-aliases", icon: Globe2,
        description: "Map free-text role/task names to canonical Task Definitions." },
      // EVENTSFLO-FINANCIAL-TAX-RULES-MATRIX-ADMIN-UI-V1
      { label: "Tax Rules Matrix", path: "/settings/financial-tax-rules", icon: CreditCard,
        description: "Admin-editable VAT / reverse-charge matrix per (client tax treatment × line tax class). Drives quote & invoice line tax decisions." },
    ],
  },
  // AI Rules — review only. Kept distinct from Live Rule Settings.
  // EVENTSFLO-AI-SHIFT-RULES-TENANT-ADMIN-MANAGEMENT-V1
  {
    id: "ai-rules",
    title: "AI Rules",
    description:
      "Used for AI interpretation review/testing only. Does not currently change saved shift rules, pricing, or supplements.",
    icon: Sparkles,
    path: "/settings/ai-shift-rules",
    color: "text-pink-500",
    bgColor: "bg-pink-500/10",
    statusBadge: {
      label: "REVIEW ONLY — not used by saved shifts",
      variant: "review-only",
    },
    items: [
      { label: "Crew Chief, Meals, Transport rules", path: "/settings/ai-shift-rules" },
      { label: "Client-specific exceptions", path: "/settings/ai-shift-rules" },
      { label: "Test AI draft interpretation", path: "/settings/ai-shift-rules" },
    ],
  },
  // C. Email & Portal Communications
  {
    id: "email-comms",
    title: "Email & Portal Communications",
    description: "Email provider configuration plus every tenant-editable email template.",
    icon: Mail,
    path: "/settings/email",
    color: "text-sky-500",
    bgColor: "bg-sky-500/10",
    items: [
      { label: "Email Configuration", path: "/settings/email", icon: Mail,
        description: "Provider connection, default sender, deliverability." },
      // EVENTSFLO-TENANT-EDITABLE-EMAIL-TEMPLATES-BILINGUAL-V1
      // EVENTSFLO-UNIFY-CREW-AND-PORTAL-EMAIL-TEMPLATES-AUDIT-V1
      { label: "Access & Invite Email Templates", path: "/settings/portal-email-templates", icon: Mail,
        description: "Client portal + crew portal invite, reset, magic link, PWA install (bilingual EN/FR)." },
      { label: "Quote / Invoice Email Templates", path: "/settings/email-templates", icon: Mail,
        description: "Editable copy for quote, invoice and reminder emails. Includes Client Portal link + PDF attachment." },
    ],
  },
  // D. Data Management
  {
    id: "data-management",
    title: "Data Management",
    description: "Import operational data, clean up duplicates, and remove test data safely.",
    icon: FileText,
    path: "/settings/data-import",
    color: "text-indigo-500",
    bgColor: "bg-indigo-500/10",
    items: [
      { label: "Data Import", path: "/settings/data-import", icon: FileText,
        description: "Bulk import projects, clients, venues, etc. ⚠ For Task Definitions / Supplements, use Rules, Tasks & Pricing → Task Definitions Export / Import." },
      { label: "Staff Import", path: "/settings/staff-import", icon: User,
        description: "Bulk import crew / staff records." },
      { label: "Data Cleanup", path: "/settings/data-cleanup", icon: Wrench,
        description: "Find and resolve duplicates / orphaned references." },
      // EVENTSFLO-SETTINGS-DELETE-EMERGENT-TEST-DATA-TOOL-V1
      { label: "Delete Emergent Test Data", path: "/settings/test-data-cleanup", icon: Wrench,
        description: "One-click removal of TEST-prefixed runtime data." },
    ],
  },
  // E. Integrations & System
  {
    id: "integrations-system",
    title: "Integrations & System",
    description: "Third-party integrations, system diagnostics and logs.",
    icon: Plug,
    path: "/settings/company/integrations",
    color: "text-orange-500",
    bgColor: "bg-orange-500/10",
    items: [
      { label: "Integrations", path: "/settings/company/integrations", icon: Plug },
      { label: "Google Integration", path: "/settings/integrations/google", icon: Plug },
      { label: "System Info", path: "/settings/system-info", icon: Server },
      { label: "Debug Tools", path: "/settings/debug-tools", icon: Bug },
      { label: "View Logs", path: "/debug/logs", icon: FileText },
    ],
  },
];

function SettingsCard({ category, onClick, onItemClick, filterText }) {
  const Icon = category.icon;
  // EVENTSFLO-SETTINGS-RULES-LABEL-ACTIVE-VS-AI-REVIEW-ONLY-V1
  const statusBadge = category.statusBadge;
  const badgeColorClass =
    statusBadge?.variant === "active"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40"
      : statusBadge?.variant === "review-only"
      ? "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40"
      : "";

  // EVENTSFLO-SETTINGS-HUB-ORGANISATION-AND-MISSING-LINKS-V1
  // Filter items by free-text search. Match label OR description.
  const normalisedFilter = (filterText || "").trim().toLowerCase();
  const items = normalisedFilter
    ? (category.items || []).filter((i) => {
        const hay = `${i.label || ""} ${i.description || ""}`.toLowerCase();
        return hay.includes(normalisedFilter);
      })
    : category.items || [];
  if (normalisedFilter && items.length === 0) return null;

  return (
    <Card
      className="hover:border-primary/50 transition-all hover:shadow-md group"
      data-testid={`settings-card-${category.id}`}
    >
      <CardHeader className="pb-3 cursor-pointer" onClick={onClick}>
        <div className="flex items-start justify-between">
          <div className={`p-3 rounded-lg ${category.bgColor}`}>
            <Icon className={`h-6 w-6 ${category.color}`} />
          </div>
          {category.superadminOnly && (
            <Badge variant="outline" className="text-xs">
              <Shield className="h-3 w-3 mr-1" />
              SuperAdmin
            </Badge>
          )}
        </div>
        <CardTitle className="text-lg mt-3 flex items-center justify-between">
          {category.title}
          <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
        </CardTitle>
        {statusBadge && (
          <Badge
            variant="outline"
            className={`text-xs mt-1 w-fit font-semibold ${badgeColorClass}`}
            data-testid={`settings-card-${category.id}-status-badge`}
          >
            {statusBadge.label}
          </Badge>
        )}
        <CardDescription>{category.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {items.map((item, index) => {
            const ItemIcon = item.icon;
            const slug = (item.label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
            return (
              <button
                key={index}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onItemClick?.(item.path);
                }}
                className="w-full flex items-start gap-2 text-left px-2 py-1.5 rounded-md text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                data-testid={`settings-item-${category.id}-${slug}`}
              >
                {ItemIcon ? (
                  <ItemIcon className="h-4 w-4 mt-0.5 shrink-0" />
                ) : (
                  <div className="w-1 h-1 rounded-full bg-muted-foreground mt-2 shrink-0" />
                )}
                <span className="flex-1 min-w-0">
                  <span className="block font-medium leading-tight">{item.label}</span>
                  {item.description && (
                    <span className="block text-xs text-muted-foreground/70 mt-0.5">
                      {item.description}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default function SettingsHubPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  // EVENTSFLO-SETTINGS-HUB-ORGANISATION-AND-MISSING-LINKS-V1
  const [filterText, setFilterText] = useState("");

  // Filter categories based on user role
  const visibleCategories = settingsCategories.filter(
    (cat) => !cat.superadminOnly || isSuperAdmin
  );

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="settings-hub-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
        <span className="hover:text-foreground cursor-pointer" onClick={() => navigate("/")}>
          Home
        </span>
        <ChevronRight className="h-4 w-4" />
        <span className="text-foreground font-medium">Settings</span>
      </div>

      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground mt-2">
            Manage platform, company, rules, email, data and system tools
          </p>
        </div>
        {/* EVENTSFLO-SETTINGS-HUB-ORGANISATION-AND-MISSING-LINKS-V1 */}
        <div className="w-full sm:w-80">
          <input
            type="text"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Filter settings… (e.g. bilingual, invite, rule)"
            data-testid="settings-hub-search"
            className="w-full px-3 py-2 text-sm rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      {/* EF-SETTINGS-CONSOLIDATION-V1 (PHASE 3): System Version Card */}
      <Card className="mb-6 bg-surface-elevated/50 border-border" data-testid="system-version-card">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Info className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-medium text-sm">System Version</h3>
                <p className="text-xs text-muted-foreground">Platform and PWA build information</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="text-right">
                <span className="text-xs text-muted-foreground block">Platform Version</span>
                <span className="font-mono font-medium" data-testid="platform-version">{BUILD_VERSION}</span>
              </div>
              <div className="text-right">
                <span className="text-xs text-muted-foreground block">PWA Version</span>
                <span className="font-mono font-medium" data-testid="pwa-version">{BUILD_VERSION}</span>
              </div>
              <Badge variant="outline" className="text-xs">
                {APP_VERSION}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {visibleCategories.map((category) => (
          <SettingsCard
            key={category.id}
            category={category}
            filterText={filterText}
            onClick={() => navigate(category.path)}
            onItemClick={(p) => navigate(p)}
          />
        ))}
      </div>

      {/* Quick Links Section */}
      <div className="mt-10 pt-6 border-t">
        <h2 className="text-lg font-semibold mb-4">Quick Access</h2>
        <div className="flex flex-wrap gap-3">
          <QuickLink 
            label="Email Templates" 
            path="/settings/email-templates" 
            onClick={() => navigate("/settings/email-templates")}
          />
          <QuickLink 
            label="Data Import" 
            path="/settings/data-import" 
            onClick={() => navigate("/settings/data-import")}
          />
          <QuickLink 
            label="Data Cleanup" 
            path="/settings/data-cleanup" 
            onClick={() => navigate("/settings/data-cleanup")}
          />
          {/* EVENTSFLO-SETTINGS-DELETE-EMERGENT-TEST-DATA-TOOL-V1 */}
          <QuickLink
            label="Delete Emergent Test Data"
            path="/settings/test-data-cleanup"
            onClick={() => navigate("/settings/test-data-cleanup")}
          />
          {isSuperAdmin && (
            <>
              <QuickLink 
                label="Tenant Management" 
                path="/admin/tenants" 
                onClick={() => navigate("/admin/tenants")}
              />
              <QuickLink 
                label="Roles & Permissions" 
                path="/admin/roles" 
                onClick={() => navigate("/admin/roles")}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function QuickLink({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="px-3 py-1.5 text-sm rounded-full border hover:bg-accent hover:text-accent-foreground transition-colors"
      data-testid={`quick-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {label}
    </button>
  );
}
